import { useState, useEffect, useMemo } from "react";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Plus, Trash2, Edit, Ban, ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PERMISSIONS = [
  { id: "view_dashboard", label: "Dashboard" },
  { id: "manage_users", label: "Users" },
  { id: "manage_employees", label: "Employees" },
  { id: "billing_access", label: "Billing" },
  { id: "view_interviews", label: "Interviews" },
  { id: "logs_access", label: "Logs" },
  { id: "view_testimonials", label: "Testimonials" },
  { id: "ai_monitor", label: "AI Monitor" },
  { id: "delete_users", label: "Delete Users" }
];

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const { t } = useAdminLanguage();

  const [formData, setFormData] = useState({ name: "", email: "", position: "", permissions: [] });
  const [editData, setEditData] = useState({ name: "", position: "", permissions: [] });

  useEffect(() => { fetchEmployees(); }, []);

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API}/api/admin/employees`, { credentials: 'include' });
      if (res.ok) setEmployees(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />;
  };

  const sorted = useMemo(() => {
    return [...employees].sort((a, b) => {
      let va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [employees, sortKey, sortDir]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/admin/employees`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData), credentials: "include"
      });
      if (res.ok) { toast.success(t("employee_created")); setDialogOpen(false); fetchEmployees(); setFormData({ name: "", email: "", position: "", permissions: [] }); }
      else toast.error(t("error_generic"));
    } catch (e) { toast.error(t("connection_error")); }
  };

  const handleEdit = async () => {
    if (!editDialog) return;
    try {
      const res = await fetch(`${API}/api/admin/employees/${editDialog}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData), credentials: "include"
      });
      if (res.ok) { toast.success(t("employee_updated")); setEditDialog(null); fetchEmployees(); }
      else toast.error(t("error_generic"));
    } catch (e) { toast.error(t("connection_error")); }
  };

  const handleSuspend = async (userId, isSuspended) => {
    const action = isSuspended ? "reactivate" : "suspend";
    try {
      const res = await fetch(`${API}/api/admin/employees/${userId}/suspend`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }), credentials: "include"
      });
      if (res.ok) { toast.success(action === "suspend" ? t("suspend") : t("reactivate")); fetchEmployees(); }
    } catch (e) { toast.error(t("connection_error")); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      const res = await fetch(`${API}/api/admin/employees/${userId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success(t("employee_deleted")); fetchEmployees(); }
    } catch (e) { toast.error(t("connection_error")); }
  };

  const togglePermission = (permId, target) => {
    const setter = target === "form" ? setFormData : setEditData;
    setter(prev => {
      const perms = prev.permissions.includes(permId) ? prev.permissions.filter(p => p !== permId) : [...prev.permissions, permId];
      return { ...prev, permissions: perms };
    });
  };

  const PermissionChecklist = ({ selected, target }) => (
    <div className="grid grid-cols-2 gap-2">
      {PERMISSIONS.map(perm => (
        <div key={perm.id} className="flex items-center space-x-2">
          <Checkbox id={`${target}-${perm.id}`} checked={selected.includes(perm.id)} onCheckedChange={() => togglePermission(perm.id, target)} className="border-zinc-700 data-[state=checked]:bg-indigo-600" />
          <label htmlFor={`${target}-${perm.id}`} className="text-sm text-zinc-300 cursor-pointer">{perm.label}</label>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">{t("employees")}</h1>
          <p className="text-zinc-500 text-sm">{t("manage_employees")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-employee-btn">
              <Plus className="w-4 h-4 mr-2" />{t("add_employee")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader><DialogTitle>{t("add_employee")}</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t("name")}</Label>
                <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="bg-zinc-950 border-zinc-800" required />
              </div>
              <div className="space-y-2">
                <Label>{t("email")}</Label>
                <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="bg-zinc-950 border-zinc-800" required />
              </div>
              <div className="space-y-2">
                <Label>Position</Label>
                <Input placeholder="Ex: RH Manager" value={formData.position} onChange={e => setFormData({ ...formData, position: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              </div>
              <div className="space-y-2">
                <Label>{t("permissions")}</Label>
                <PermissionChecklist selected={formData.permissions} target="form" />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">{t("save")}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("name")}>
                  <span className="flex items-center">{t("name")}<SortIcon col="name" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("role")}>
                  <span className="flex items-center">{t("role")}<SortIcon col="role" /></span>
                </TableHead>
                <TableHead className="text-zinc-400">{t("permissions")}</TableHead>
                <TableHead className="text-zinc-400">{t("status")}</TableHead>
                <TableHead className="text-right text-zinc-400">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(emp => (
                <TableRow key={emp.user_id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-purple-500/20 rounded-full flex items-center justify-center text-purple-400 font-bold text-xs">{emp.name?.[0]}</div>
                      <div>
                        <p className="text-white text-sm font-medium">{emp.name}</p>
                        <p className="text-xs text-zinc-500">{emp.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-purple-500 text-purple-400 bg-purple-500/10">{t(emp.role)}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {emp.role === 'super_admin' ? (
                        <Badge className="bg-zinc-800 text-zinc-400 text-xs">ALL ACCESS</Badge>
                      ) : (
                        emp.permissions?.map(p => <Badge key={p} className="bg-zinc-800 text-zinc-400 text-xs">{p.replace('_', ' ')}</Badge>)
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={emp.is_suspended ? "bg-red-500/10 text-red-400" : "bg-emerald-500/10 text-emerald-400"}>
                      {emp.is_suspended ? t("suspended") : t("active")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {emp.role !== 'super_admin' && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-blue-400" onClick={() => { setEditDialog(emp.user_id); setEditData({ name: emp.name, position: emp.position || "", permissions: emp.permissions || [] }); }} title={t("edit")}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-orange-400" onClick={() => handleSuspend(emp.user_id, emp.is_suspended)} title={emp.is_suspended ? t("reactivate") : t("suspend")}>
                          <Ban className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-red-400" onClick={() => handleDelete(emp.user_id)} title={t("delete")}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader><DialogTitle>{t("edit")} {t("admin_employee")}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t("name")}</Label>
              <Input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div className="space-y-2">
              <Label>Position</Label>
              <Input value={editData.position || ""} onChange={e => setEditData({ ...editData, position: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div className="space-y-2">
              <Label>{t("permissions")}</Label>
              <PermissionChecklist selected={editData.permissions} target="edit" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditDialog(null)}>{t("cancel")}</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleEdit}>{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
